import { useState } from "react";
import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { useAttachments, useAttachmentStats } from "../../../hooks/useDocuments";
import type { AttachmentEntry } from "../../../server/documents";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { queryKeys, STALE_TIMES } from "../../../lib/queryClient";
import { getAttachmentStats } from "../../../server/documents";
import type { RouterContextWithSession } from "../../../router";

export const Route = createFileRoute("/_admin/documents/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;

    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.documents.stats(),
      queryFn: async () => {
        const result = await getAttachmentStats({
          data: { accessToken: session.access_token },
        });
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: STALE_TIMES.system,
    });
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("Documents"),
  component: DocumentsPage,
});

function DocumentsPage() {
  const { session, loading, isAuthenticated } = useAuth();

  if (loading || !isAuthenticated || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return <AuthenticatedDocuments accessToken={session.access_token} />;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function AuthenticatedDocuments({ accessToken }: { accessToken: string }) {
  const auth = { accessToken };
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const limit = 20;

  const { data: stats, isLoading: statsLoading } = useAttachmentStats(auth);
  const { data, isLoading, error } = useAttachments(auth, { page, limit, search: search || undefined });

  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <div className="text-foreground">
      <div className="container mx-auto px-4 py-6 max-w-5xl space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Documents & Attachments</h1>
            <p className="text-muted-foreground">
              Manage files and attachments across your workspace
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/dashboard">Back to Dashboard</Link>
          </Button>
        </header>

        {/* Stats Cards */}
        {!statsLoading && stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground">Total Attachments</p>
                <p className="text-2xl font-semibold">{stats.totalAttachments}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground">Total Storage</p>
                <p className="text-2xl font-semibold">{formatBytes(stats.totalStorageBytes)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground">Most Common Type</p>
                <p className="text-2xl font-semibold">{stats.mostCommonType}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search & File List */}
        <Card>
          <CardHeader>
            <CardTitle>Attachments</CardTitle>
            <CardDescription>
              {data ? `${data.total} files found` : "Loading..."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search files..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                aria-label="Search files"
              />
              <Button variant="outline" size="sm" onClick={handleSearch}>
                Search
              </Button>
            </div>

            {isLoading && (
              <p className="text-muted-foreground">Loading files...</p>
            )}
            {error && (
              <p className="text-destructive">Error: {error.message}</p>
            )}
            {!isLoading && !error && (!data || data.attachments.length === 0) && (
              <p className="text-muted-foreground">No attachments found.</p>
            )}
            {!isLoading && !error && data && data.attachments.length > 0 && (
              <div className="space-y-4">
                <ul className="space-y-2">
                  {data.attachments.map((a: AttachmentEntry) => (
                    <li
                      key={a.id}
                      className="flex items-center justify-between rounded-lg border bg-card p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">
                          {a.display_name || a.file_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {a.file_type ?? "unknown"} · {formatBytes(a.file_size)} ·{" "}
                          {new Date(a.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <p className="text-sm text-muted-foreground">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
